'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, use, type ReactNode } from 'react';
import { MEDIA_OVERLAY_STACK_MAX_SHIFT, type MobilePlayPosition } from '@/constants/content-detail-layout';
import { CONSENT_BANNER_CLEARANCE } from '@/constants/cookie-consent';
import { useUAContext } from '@/context/UAContext';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { useConsentBannerHeight } from '@/hooks/useConsentBannerHeight';
type MobilePlayCtaPlacementContextType = {
  playPosition: MobilePlayPosition;
  /** Whether this visitor had already dismissed the consent banner before this page view. */
  hasDismissedConsent: boolean;
  /**
   * Distance from the viewport bottom at which the mobile Play CTA should float to clear the
   * consent banner, or `0` when it should stay in its normal page-flow position.
   */
  ctaBannerOffset: number;
  /**
   * Extra bottom inset for the landscape media overlay stack, so the Play CTA inside it clears the
   * consent banner. `0` when the stack should sit at its resting position.
   */
  mediaStackBannerOffset: number;
};
const MobilePlayCtaPlacementContext = createContext<MobilePlayCtaPlacementContextType | null>(null);
type MobilePlayCtaPlacementProviderProps = {
  playPosition: MobilePlayPosition;
  hasDismissedConsent: boolean;
  children: ReactNode;
};
export function MobilePlayCtaPlacementProvider(t0) {
  const $ = _c(8);
  const {
    playPosition,
    hasDismissedConsent,
    children
  } = t0;
  const {
    device,
    isMobileLandscape
  } = useUAContext();
  const {
    showMobilePlayButton
  } = useWorldDetailsContext();
  const isMobileBrowserLandscape = isMobileLandscape && device === "mobile_browser";
  const needsBannerHeight = showMobilePlayButton && (isMobileBrowserLandscape || playPosition === "test");
  const consentBannerHeight = useConsentBannerHeight(needsBannerHeight && !hasDismissedConsent);
  const bannerClearance = consentBannerHeight > 0 ? consentBannerHeight + CONSENT_BANNER_CLEARANCE : 0;
  const ctaBannerOffset = needsBannerHeight && !isMobileLandscape && playPosition === "test" ? bannerClearance : 0;
  const mediaStackBannerOffset = needsBannerHeight && isMobileBrowserLandscape ? Math.min(bannerClearance, MEDIA_OVERLAY_STACK_MAX_SHIFT) : 0;
  let t1;
  if ($[0] !== ctaBannerOffset || $[1] !== hasDismissedConsent || $[2] !== mediaStackBannerOffset || $[3] !== playPosition) {
    t1 = {
      playPosition,
      hasDismissedConsent,
      ctaBannerOffset,
      mediaStackBannerOffset
    };
    $[0] = ctaBannerOffset;
    $[1] = hasDismissedConsent;
    $[2] = mediaStackBannerOffset;
    $[3] = playPosition;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== children || $[6] !== t1) {
    t2 = <MobilePlayCtaPlacementContext value={t1}>{children}</MobilePlayCtaPlacementContext>;
    $[5] = children;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function useMobilePlayCtaPlacementContext() {
  const ctx = use(MobilePlayCtaPlacementContext);
  if (!ctx) throw new Error('useMobilePlayCtaPlacementContext must be used within MobilePlayCtaPlacementProvider');
  return ctx;
}